import { z } from 'zod';

import { listQuerySchema } from './common.js';

/**
 * RAKİP ANALİZİ — Genişleme Adım 10 sözleşmesi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * İki kaynak, tek ekran — ve neden tek tabloda birleşmiyorlar
 * ═══════════════════════════════════════════════════════════════════════════
 *   Meta Ad Library (`ads_archive`) → rakip NE DİYOR. Kreatif metni, ne
 *                                     zamandan beri yayında. Nitel.
 *   Google Auction Insights          → rakip SENİ NEREDE GEÇİYOR. Aynı açık
 *                                     artırmadaki pay, üstte çıkma oranı.
 *                                     Nicel, günlük zaman serisi.
 *
 * Şekilleri farklı olduğu için ayrı tablolar ve bu dosyada ayrı tipler. Aynı
 * "rakip" nesnesinde birleştirmek MARKA kavramı ister (bir işletmenin hem
 * Facebook sayfası hem alan adı) — o kavram bu adımın kapsamı dışında ve
 * uydurulmuş bir eşleştirme, iki kaynağın sayılarını yanlış rakibe yazardı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 AD LIBRARY `AdPlatformConnector` ARKASINA GİRMİYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * 14 Ağu 2026'da ölçüldü: `ads_archive` uygulama jetonuyla (`app_id|app_secret`)
 * çağrıldığında `code 10 / error_subcode 2332004 — "App role required"` dönüyor.
 * Uç, reklam hesabı jetonu istemiyor ama KİMLİK ONAYLI bir kullanıcı jetonu
 * istiyor. Connector sözleşmesinin her metodu jetonu parametre alıyor ve o
 * jeton KİRACININ bağlantısından çözülüyor; Ad Library'nin jetonu ise
 * platforma ait. Arayüze sokmak, sözleşmenin tek anlamlı parametresini yalan
 * hâline getirirdi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ DSA KISITI — ürünün Türkiye'deki kullanımını doğrudan etkiliyor
 * ═══════════════════════════════════════════════════════════════════════════
 * `ads_archive` TİCARİ reklamları yalnızca AB ülkeleri için döndürüyor
 * (politik/toplumsal reklamlar global). Sorgular bu yüzden EU27 ile yapılıyor.
 *
 * Bunun ölçülmüş sonucu: eski kurulumun ürettiği 11 gerçek satırın hepsi TÜRK
 * reklamverenlere ait (`languages: ["tr"]`, Türkçe metin) ama `eu_total_reach`
 * değerleri 5–10 — yani AB'ye TAŞAN paylarıyla görünüyorlar. Sadece TR'ye
 * yayın yapan bir rakip HİÇ görünmez. Arayüz boş durumda bunu açıkça
 * söylemeli; "rakibin reklamı yok" demek yanlış olur.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Ad Library ham satırı ve normalize edilmiş hâli
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `ads_archive`in döndürdüğü satır — KULLANDIĞIMIZ alt küme.
 *
 * Alanların neredeyse hepsi opsiyonel ve bu API'nin gerçek davranışı, savunma
 * refleksi değil: ölçülen gerçek satırlarda `ad_creative_link_titles`,
 * `ad_creative_link_descriptions`, `ad_delivery_stop_time` ve `currency`
 * HİÇ GELMEDİ. Alanlar reklamın türüne göre değişiyor.
 *
 * `impressions` / `spend` bantları BİLEREK YOK: Meta onları yalnız politik ve
 * toplumsal konu reklamlarında veriyor. Ticari reklamda tek erişim sinyali
 * `eu_total_reach` ve o da yalnız AB'yi sayıyor.
 */
export interface AdsArchiveRow {
  id?: string;
  page_id?: string;
  page_name?: string;
  ad_creation_time?: string;
  ad_creative_bodies?: string[];
  ad_creative_link_titles?: string[];
  ad_creative_link_captions?: string[];
  ad_creative_link_descriptions?: string[];
  ad_delivery_start_time?: string;
  ad_delivery_stop_time?: string;
  ad_snapshot_url?: string;
  publisher_platforms?: string[];
  languages?: string[];
  eu_total_reach?: number;
  currency?: string;
}

/// `ads_archive` `fields` parametresi. Tek yerde çünkü istemci ile eşleyici
/// aynı listeyi görmek zorunda: eşleyicinin okuduğu ama istenmeyen bir alan
/// sessizce hep `undefined` gelirdi.
export const ADS_ARCHIVE_FIELDS = [
  'id',
  'page_id',
  'page_name',
  'ad_creation_time',
  'ad_creative_bodies',
  'ad_creative_link_titles',
  'ad_creative_link_captions',
  'ad_creative_link_descriptions',
  'ad_delivery_start_time',
  'ad_delivery_stop_time',
  'ad_snapshot_url',
  'publisher_platforms',
  'languages',
  'eu_total_reach',
  'currency',
] as const;

/**
 * 27 AB üyesi (ISO 3166-1 alfa-2) — `ad_reached_countries`.
 *
 * Liste SABİT ve tam: eksik bir ülke, o ülkeye yayın yapan rakibin sessizce
 * görünmemesi demek. Birleşik Krallık YOK (AB üyesi değil, DSA kapsamı dışı);
 * eklemek boş sonuç değil HATA döndürür.
 */
export const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
] as const;

/// Eşleyicinin ürettiği satır — veritabanına yazılmaya hazır, ama kiracı
/// kimlikleri (`workspaceId`, `profileId`) HARİÇ. Saf fonksiyonun bizim
/// veritabanımızı bilmesi gerekmiyor.
export interface NormalizedCompetitorAd {
  adArchiveId: string;
  pageId: string;
  /// Satırda görülen sayfa adı. Profilin `pageName`ini tazelemek için:
  /// kullanıcı profili kimlikle eklemiş olabilir ya da rakip adını
  /// değiştirmiş olabilir. `null` = API vermedi, mevcut ad korunuyor.
  pageName: string | null;
  bodies: string[];
  linkTitles: string[];
  linkCaptions: string[];
  linkDescriptions: string[];
  /// Jetonu temizlenmiş arşiv adresi. `null` = API adres vermedi.
  snapshotUrl: string | null;
  creationTime: Date | null;
  deliveryStart: Date | null;
  /// `null` = reklam hâlâ yayında (ölçülen gerçek satırların tamamında böyle).
  deliveryStop: Date | null;
  publisherPlatforms: string[];
  languages: string[];
  euTotalReach: number | null;
  currency: string | null;
  /// Saniteli ham satır — jeton temizlendikten SONRA.
  raw: AdsArchiveRow;
}

/**
 * 🔴 `ad_snapshot_url` BİZİM JETONUMUZU TAŞIYOR.
 *
 * `ads_archive` bu adresi `…/render_ad/?id=<id>&access_token=<JETON>` biçiminde
 * döndürüyor. Veritabanına ya da arayüze ham hâliyle yazmak, platform
 * jetonunu her okuyana vermek demek — ve o jeton kiracıya değil BİZE ait,
 * yani sızıntı tek bir müşteriyle sınırlı kalmazdı.
 *
 * Silinen parametreler: `access_token` ve `token` (Meta ikisini de kullanmış
 * durumda görüldü). Diğer parametreler korunuyor — adresi yeniden kurmak
 * yerine yalnız zararlı olanı çıkarmak, biçim değişirse adresi tamamen
 * bozmaktan iyi.
 *
 * `http`/`https` ile başlamayan adres `null` DÖNÜYOR, ham hâliyle geçmiyor:
 * "URL bozuk" ile "jeton sızdı" arasında seçim yapmak zorundaysak birincisi.
 *
 * ⚠️ `new URL` KULLANILMIYOR ve bu bir tercih değil bir kısıt: bu paket
 * `types: []` + `lib: ["ES2023"]` ile derleniyor (tsconfig.build.json'daki
 * "Frontend'de de kullanılıyor — Node tiplerine bağlanmamalı" kararı), yani
 * `URL` global'i burada TANIMLI DEĞİL. Düz dize işlemi ayrıca daha sadık:
 * `URL` adresi normalize ediyor (kodlama, sondaki eğik çizgi) ve
 * karşılaştırdığımız değer Meta'nın verdiğinden farklı çıkabilirdi.
 */
export function sanitizeSnapshotUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;

  // Parça (`#…`) sorgu dizesinin dışında — önce ayrılıyor, yoksa son
  // parametrenin değerine yapışır ve silme kontrolü şaşardı.
  const hashAt = trimmed.indexOf('#');
  const hash = hashAt === -1 ? '' : trimmed.slice(hashAt);
  const withoutHash = hashAt === -1 ? trimmed : trimmed.slice(0, hashAt);

  const queryAt = withoutHash.indexOf('?');
  if (queryAt === -1) return withoutHash + hash;

  const base = withoutHash.slice(0, queryAt);
  const kept = withoutHash
    .slice(queryAt + 1)
    .split('&')
    .filter((pair) => {
      if (pair.length === 0) return false;
      const eq = pair.indexOf('=');
      const name = (eq === -1 ? pair : pair.slice(0, eq)).toLowerCase();
      return name !== 'access_token' && name !== 'token';
    });

  return kept.length === 0 ? base + hash : `${base}?${kept.join('&')}${hash}`;
}

/**
 * `ads_archive` satırını normalize eder. SAF — ağ, saat, veritabanı yok.
 *
 * `null` DÖNÜYOR (fırlatmıyor) iki durumda: `id` yok ya da `page_id` yok.
 * İkisi de tekillik anahtarının parçası; onlarsız satır ne yazılabilir ne
 * güncellenebilir. Fırlatmak, tek bozuk satır yüzünden 500 satırlık bir
 * senkronu düşürürdü — çağıran taraf `null`ları sayıp raporluyor
 * ("3 satır atlandı"), sessizce yutmuyor.
 *
 * ⚠️ `pageId` satırın kendisinden okunuyor, profilden DEĞİL. Meta bir sayfayı
 * birleştirdiğinde arşiv satırı yeni kimliği taşıyabiliyor; profilinkini
 * yazmak, veriyi gerçekte ait olmadığı kimliğe bağlamak olurdu.
 */
export function mapAdsArchiveRow(row: AdsArchiveRow): NormalizedCompetitorAd | null {
  const adArchiveId = nonEmpty(row.id);
  const pageId = nonEmpty(row.page_id);
  if (!adArchiveId || !pageId) return null;

  const snapshotUrl = sanitizeSnapshotUrl(row.ad_snapshot_url);

  // Ham satır da saniteleniyor: `raw` alanı veritabanına gidiyor ve içinde
  // temizlenmemiş bir adres bırakmak, üstteki temizliği anlamsız kılardı.
  const raw: AdsArchiveRow = { ...row };
  if (raw.ad_snapshot_url !== undefined) {
    const cleaned = sanitizeSnapshotUrl(raw.ad_snapshot_url);
    if (cleaned === null) delete raw.ad_snapshot_url;
    else raw.ad_snapshot_url = cleaned;
  }

  return {
    adArchiveId,
    pageId,
    pageName: nonEmpty(row.page_name),
    bodies: cleanStrings(row.ad_creative_bodies),
    linkTitles: cleanStrings(row.ad_creative_link_titles),
    linkCaptions: cleanStrings(row.ad_creative_link_captions),
    linkDescriptions: cleanStrings(row.ad_creative_link_descriptions),
    snapshotUrl,
    creationTime: parseArchiveDate(row.ad_creation_time),
    deliveryStart: parseArchiveDate(row.ad_delivery_start_time),
    deliveryStop: parseArchiveDate(row.ad_delivery_stop_time),
    publisherPlatforms: cleanStrings(row.publisher_platforms),
    languages: cleanStrings(row.languages),
    euTotalReach: finiteInt(row.eu_total_reach),
    currency: nonEmpty(row.currency),
    raw,
  };
}

/**
 * `ads_archive` tarihleri iki biçimde geliyor: `YYYY-MM-DD` (ölçülen gerçek
 * satırlarda hep bu) ve tam ISO damgası. `new Date` ikisini de anlıyor.
 *
 * Geçersiz değer `null` — `Invalid Date` yazmak Prisma'da çalışma zamanı
 * hatası verir ve o hata senkronun ortasında, teşhis edilemez bir yerde
 * patlar.
 */
export function parseArchiveDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? `${trimmed}T00:00:00Z` : trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Reklamın kaç GÜN yayında kaldığı.
 *
 * `deliveryStop` yoksa `lastSeenAt`e kadar sayılıyor — hâlâ yayında demek ve
 * "bugüne kadar N gün" doğru cevap. Bu sayı ürünün asıl sinyali: rakibin
 * uzun süredir döndürdüğü kreatif, işine YARAYAN kreatiftir.
 *
 * `null` = başlangıç bilinmiyor. `0` DEĞİL: sıfır "aynı gün başlayıp bitti"
 * demek ve bu gerçek bir değer.
 *
 * Dahil sayım (`+1`): 1 Ocak'ta başlayıp 1 Ocak'ta görülen reklam 0 değil
 * 1 gün yayında kaldı.
 */
export function daysRunning(
  ad: Pick<NormalizedCompetitorAd, 'deliveryStart' | 'deliveryStop'>,
  lastSeenAt: Date,
): number | null {
  if (!ad.deliveryStart) return null;
  const end = ad.deliveryStop ?? lastSeenAt;
  const ms = end.getTime() - ad.deliveryStart.getTime();
  if (ms < 0) return 0;
  return Math.floor(ms / 86_400_000) + 1;
}

// ═══════════════════════════════════════════════════════════════════════════
// Uç sözleşmeleri
// ═══════════════════════════════════════════════════════════════════════════

/// Meta sayfa kimliği — YALNIZ rakam. Kullanıcı arayüze sayfa adını ya da
/// profil adresini yapıştırabildiği için doğrulama sözleşmede: sunucu ile
/// arayüz aynı reddi vermek zorunda.
export const metaPageIdSchema = z
  .string()
  .trim()
  .regex(/^\d{5,25}$/, 'Meta sayfa kimliği yalnız rakamlardan oluşur');

export const competitorProfileCreateSchema = z.object({
  pageId: metaPageIdSchema,
  /// Boş bırakılırsa ilk senkronda API'nin döndürdüğü ad yazılır. Zorunlu
  /// yapmak, kullanıcıyı Meta'da adı aramaya zorlardı.
  pageName: z.string().trim().min(1).max(200).optional(),
  notes: z.string().trim().max(2000).nullish(),
});
export type CompetitorProfileCreateInput = z.infer<typeof competitorProfileCreateSchema>;

export const competitorProfileUpdateSchema = z
  .object({
    pageName: z.string().trim().min(1).max(200).optional(),
    notes: z.string().trim().max(2000).nullish(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'En az bir alan gerekli');
export type CompetitorProfileUpdateInput = z.infer<typeof competitorProfileUpdateSchema>;

export const competitorProfileListQuerySchema = listQuerySchema.extend({
  /// Varsayılan `undefined` = hepsi. Pasif profiller SİLİNMİŞ değil, o yüzden
  /// varsayılan olarak gizlenmiyorlar.
  isActive: z.coerce.boolean().optional(),
});
export type CompetitorProfileListQuery = z.infer<typeof competitorProfileListQuerySchema>;

export const competitorAdListQuerySchema = listQuerySchema.extend({
  profileId: z.string().uuid().optional(),
  /// `true` = yalnız son senkronda görülenler.
  isActive: z.coerce.boolean().optional(),
});
export type CompetitorAdListQuery = z.infer<typeof competitorAdListQuerySchema>;

export interface CompetitorProfileRow {
  id: string;
  pageId: string;
  pageName: string;
  notes: string | null;
  isActive: boolean;
  lastSyncedAt: string | null;
  /// Son senkron hatası — arayüz kartta uyarı basar. `null` = sorun yok.
  lastSyncError: string | null;
  activeAdCount: number;
  createdAt: string;
}

export interface CompetitorAdRow {
  id: string;
  profileId: string;
  adArchiveId: string;
  pageId: string;
  bodies: string[];
  linkTitles: string[];
  linkCaptions: string[];
  linkDescriptions: string[];
  /// ⚠️ HERKESE AÇIK DEĞİL — Facebook oturumu ister. Arayüz "Meta'da aç"
  /// bağlantısı verir, gömülü önizleme DENEMEZ.
  snapshotUrl: string | null;
  deliveryStart: string | null;
  deliveryStop: string | null;
  isActive: boolean;
  publisherPlatforms: string[];
  languages: string[];
  /// Yalnız AB'ye ulaşan tekil kişi. Arayüz bunu "erişim" diye ETİKETLEMEZ.
  euTotalReach: number | null;
  firstSeenAt: string;
  lastSeenAt: string;
  /// `daysRunning` ile hesaplanmış. `null` = başlangıç tarihi bilinmiyor.
  daysRunning: number | null;
}

/// Senkron sonucu — "sessiz düşme yasak" kuralının uygulanabilir hâli.
/// `skipped` ve `truncated` çağırana geri veriliyor ki arayüz kısmi bir
/// sonucu tam sanmasın.
export interface CompetitorSyncResult {
  profileId: string;
  fetched: number;
  created: number;
  updated: number;
  /// Son turda görülmediği için pasife alınanlar.
  deactivated: number;
  /// `id`/`page_id` taşımadığı için eşlenemeyen satırlar.
  skipped: number;
  /// Sayfa tavanına ya da kotaya takılıp kesildi mi.
  truncated: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// AI içgörü raporu
// ═══════════════════════════════════════════════════════════════════════════

export const competitorInsightRequestSchema = z.object({
  /// `null`/boş = çalışma alanının TÜM aktif rakipleri karşılaştırmalı.
  profileId: z.string().uuid().nullish(),
});
export type CompetitorInsightRequestInput = z.infer<typeof competitorInsightRequestSchema>;

/**
 * Raporun `result` JSON şekli.
 *
 * 🔴 `likelyWinners` bir TAHMİN ve adı bunu söylüyor. Elimizde rakibin
 * performans verisi YOK — Meta harcama/gösterim bantlarını ticari reklamda
 * vermiyor. Tek dolaylı sinyal süre: uzun süredir dönen kreatif muhtemelen
 * çalışıyordur. "En iyi performans gösteren reklamlar" demek, sahip
 * olmadığımız bir ölçümü varmış gibi sunmak olurdu.
 */
export interface CompetitorInsightResult {
  summary: string;
  /// Rakibin kullandığı mesaj açıları — her biri hangi reklamlardan
  /// çıkarıldığını `evidence` ile söylüyor (uydurma iddiaya karşı).
  messagingAngles: { angle: string; evidence: string[] }[];
  creativePatterns: string[];
  likelyWinners: { adArchiveId: string; daysRunning: number | null; why: string }[];
  recommendations: { title: string; detail: string }[];
  model: string;
  generatedAt: string;
}

export interface CompetitorInsightRow {
  id: string;
  profileId: string | null;
  status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELED';
  adCount: number;
  result: CompetitorInsightResult | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `/competitors` uçlarının hata kodları (29 Eyl 2026).
 *
 * Eskiden istisnalar yalnız Türkçe bir cümle taşıyordu. API tek dilli; bir
 * istemci (panel, MCP, geliştirici API'si) dallanmayı ancak koddan yapabilir.
 */
export const CompetitorErrorCode = {
  /// Aynı Meta sayfası bu çalışma alanında zaten izleniyor.
  ALREADY_TRACKED: 'COMPETITOR_ALREADY_TRACKED',
  /// Pasif profil senkronlanmaz — önce yeniden etkinleştirilmeli.
  PROFILE_INACTIVE: 'COMPETITOR_PROFILE_INACTIVE',
  /// Rapor için kayıtlı rakip reklamı yok; ilk senkron henüz bitmedi.
  NO_ADS_YET: 'COMPETITOR_NO_ADS_YET',
  PROFILE_NOT_FOUND: 'COMPETITOR_PROFILE_NOT_FOUND',
  INSIGHT_NOT_FOUND: 'COMPETITOR_INSIGHT_NOT_FOUND',
} as const;
export type CompetitorErrorCode = (typeof CompetitorErrorCode)[keyof typeof CompetitorErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// Google Auction Insights
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Google'ın rakip raporu — bir açık artırmada bizimle birlikte görünen alan
 * adları.
 *
 * ⚠️ Bu satırlar `metrics_daily`ye YAZILMAZ. Kırılımları farklı: bir
 * kampanya-gün, kaç rakip varsa o kadar satır doğuruyor.
 *
 * 🔴 ORANLAR TOPLANMAZ ve gösterim payının aksine TÜRETİLEMEZ de: rakibin
 * gösterim sayısı elimizde yok, yani ağırlıklı ortalamanın paydası yok.
 * Bu yüzden aralık sorgusu GÜN BAŞINA değer döndürüyor ve arayüz seriyi
 * çiziyor — tek bir "dönem payı" sayısı üretmiyoruz çünkü üretilemiyor.
 */
export interface NormalizedAuctionInsight {
  campaignPlatformRef: string;
  /// `YYYY-MM-DD`.
  date: string;
  domain: string;
  impressionShare: number | null;
  overlapRate: number | null;
  positionAboveRate: number | null;
  topOfPageRate: number | null;
  absoluteTopOfPageRate: number | null;
  outrankingShare: number | null;
}

export const auctionInsightQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  campaignId: z.string().uuid().optional(),
  /// Kaç rakip alan adı döndürülecek. Google bir kampanyada onlarca alan adı
  /// gösterebiliyor; hepsini çizmek okunamaz bir grafik üretir.
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type AuctionInsightQuery = z.infer<typeof auctionInsightQuerySchema>;

/// Alan adı başına özet — sıralama için ORTALAMA değil, gün sayısı ve
/// görüldüğü gün başına değerler taşıyor.
export interface AuctionInsightDomainRow {
  domain: string;
  /// Kapsamda kaç gün bu rakip görüldü. Tek günlük bir rakiple 30 gün boyunca
  /// karşımıza çıkan rakip aynı listede duramaz; sıralama bunu kullanıyor.
  daysSeen: number;
  /// Günlük seri — arayüz çizgi grafiği bundan kuruyor.
  points: {
    date: string;
    impressionShare: number | null;
    overlapRate: number | null;
    positionAboveRate: number | null;
    outrankingShare: number | null;
  }[];
}

export interface AuctionInsightResponse {
  from: string;
  to: string;
  /// `daysSeen` azalan sırada, `limit` kadar.
  domains: AuctionInsightDomainRow[];
  /// 🔴 Kapsamda hiç Google arama kampanyası yoksa `true`. Boş `domains` ile
  /// karıştırılmamalı: "rakip görülmedi" ile "bu rapor bu kapsamda anlamsız"
  /// farklı cevaplar ve arayüz farklı şey yazmalı.
  unsupportedScope: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Ad Library platform jetonu (yönetici)
// ═══════════════════════════════════════════════════════════════════════════

export const adLibraryConfigUpdateSchema = z.object({
  /// Kimlik onaylı bir hesabın uzun ömürlü `ads_read` jetonu. Boş dize =
  /// jetonu SİL (yapılandırmayı kaldır); `undefined` = dokunma.
  accessToken: z.string().trim().max(1000).optional(),
  enabled: z.boolean().optional(),
});
export type AdLibraryConfigUpdateInput = z.infer<typeof adLibraryConfigUpdateSchema>;

/// 🔴 Jetonun KENDİSİ hiçbir zaman dönmüyor — yalnız var olup olmadığı.
/// Yönetici panelinde jetonu göstermek, ekranı gören herkese platform
/// erişimi vermek olurdu.
export interface AdLibraryConfigRow {
  configured: boolean;
  enabled: boolean;
  tokenExpiresAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

// ── Yardımcılar ─────────────────────────────────────────────────────────────

function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/// Boş ve boşluktan ibaret öğeleri atar. API bazı reklamlarda tek elemanlı
/// boş dizi döndürüyor; onu saklamak arayüzde boş bir metin balonu çizerdi.
function cleanStrings(values: string[] | null | undefined): string[] {
  if (!Array.isArray(values)) return [];
  return values.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
}

/// `eu_total_reach` bazı satırlarda dize geliyor. `null` = alan yok ya da
/// sayıya çevrilemedi — `0` yazmak "hiç kimseye ulaşmadı" demek olurdu.
function finiteInt(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(n) ? Math.trunc(n) : null;
}
